import { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Button,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import Iconify from '../../../components/iconify';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import LeaveStatusBadge from './LeaveStatusBadge';
import { LEAVE_TYPE_LABELS, LEAVE_STATUS } from '../constants';
import { getLeaveById, updateLeaveStatus, cancelLeave, cancelApprovedLeave, updateLeaveRequest } from '../../../services/leaveService';
import { isAdmin, isManager, isHR, checkComponentPermission, isWfhCompany } from '../../../utils/share';
import LeaveRequestForm from './LeaveRequestForm';
import EmployeePlanDialog from './EmployeePlanDialog';
import Loading from '../../../layouts/loading';
import { getFileDownloadUrl } from '../../../services/fileUploadService';
import { useStyles } from '../styles/LeaveDetails';

const SYSTEM_AUTO_LEAVE_REASON = 'SYSTEM_AUTO_APPROVED';

const queryClient = new QueryClient();

const LeaveDetailsContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.login?.user || null);
  const userId = user?.id;
  const [leave, setLeave] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelApprovedDialogOpen, setCancelApprovedDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employeeImageUrl, setEmployeeImageUrl] = useState(null);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [editConfirmDialogOpen, setEditConfirmDialogOpen] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const classes = useStyles();
  const isAdminOrManagerOrHR = isAdmin() || isManager() || isHR();
  const isOwnLeave = leave?.userId === userId;
  // Admin can approve all leaves (including own), Manager/HR can approve only employee leaves (not own)
  const canApproveReject =
    (isAdmin() || (isManager() && !isOwnLeave)) &&
    checkComponentPermission('APPROVE_LEAVE') &&
    leave?.status === LEAVE_STATUS.PENDING;
  const canCancel = isOwnLeave && leave?.status === LEAVE_STATUS.PENDING && checkComponentPermission('DELETE_LEAVE');
  const canCancelApproved =
    (isAdmin() || isManager()) &&
    checkComponentPermission('APPROVE_LEAVE') &&
    leave?.status === LEAVE_STATUS.APPROVED;
  const canAdminEdit = isAdmin() && checkComponentPermission('UPDATE_LEAVE') && checkComponentPermission('APPROVE_LEAVE');

  const isUnplannedLeave = (leaveItem) => {
    if (!leaveItem?.startDate || !leaveItem?.createdAt) return false;
    const start = dayjs(leaveItem.startDate).startOf('day');
    const created = dayjs(leaveItem.createdAt).startOf('day');
    return created.isAfter(start);
  };
  const isSystemApprovedLeave = (leaveItem) => leaveItem?.reason === SYSTEM_AUTO_LEAVE_REASON;

  const fetchLeaveDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getLeaveById(id);
      const leaveData = response?.data?.data;
      setLeave(leaveData);

      // Fetch employee profile image if available and user is admin/manager/HR
      if (isAdminOrManagerOrHR && leaveData?.employee?.profile?.profileImage) {
        try {
          const imageUrl = await getFileDownloadUrl(leaveData.employee.profile.profileImage);
          setEmployeeImageUrl(imageUrl);
        } catch (error) {
          console.error('Error fetching employee image:', error);
          // If image fetch fails, just continue without showing image
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error fetching leave details');
      navigate('/leaves');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Clicking another request inside the plan calendar lands back here with a new id
    setPlanDialogOpen(false);
    fetchLeaveDetails();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await updateLeaveStatus(id, { status: 'approved' });
      toast.success('Leave request approved successfully');
      setApproveDialogOpen(false);
      fetchLeaveDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error approving leave request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setIsSubmitting(true);
    try {
      await updateLeaveStatus(id, { status: 'rejected', rejectionReason: rejectionReason.trim() });
      toast.success('Leave request rejected successfully');
      setRejectDialogOpen(false);
      setRejectionReason('');
      fetchLeaveDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error rejecting leave request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setIsSubmitting(true);
    try {
      await cancelLeave(id);
      toast.success('Leave request deleted successfully');
      setCancelDialogOpen(false);
      navigate('/leaves');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting leave request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (values) => {
    try {
      await updateLeaveRequest(id, values);
      toast.success('Leave request updated successfully');
      setEditFormOpen(false);
      fetchLeaveDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error updating leave request');
      throw error;
    }
  };

  const handleCancelApproved = async () => {
    setIsSubmitting(true);
    try {
      await cancelApprovedLeave(id, { cancellationReason: cancellationReason.trim() || undefined });
      toast.success('Leave cancelled successfully');
      setCancelApprovedDialogOpen(false);
      setCancellationReason('');
      fetchLeaveDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error cancelling leave request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Leaves',
      path: '/leaves',
    },
    {
      label: 'Leave Details',
      path: '',
    },
  ];

  if (isLoading) {
    return <Loading open={isLoading} />;
  }

  if (!leave) {
    return null;
  }

  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Box sx={{ maxWidth: 1000, margin: 'auto', padding: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Leave Request Details</Typography>
          <Stack direction="row" spacing={1}>
            {isAdminOrManagerOrHR && (
              <Button
                variant="outlined"
                startIcon={<Iconify icon="eva:calendar-outline" />}
                onClick={() => setPlanDialogOpen(true)}
              >
                View plan in calendar
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:arrow-back-fill" />}
              onClick={() => navigate('/leaves')}
            >
              Back to Leaves
            </Button>
          </Stack>
        </Stack>

        <Card className={classes.leaveDetailsCard}>
          <CardContent>
            <Stack spacing={1.5}>
              {/* Header with Status */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Leave Information</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LeaveStatusBadge status={leave.status} />
                  {isSystemApprovedLeave(leave) && (
                    <Chip label="System Approved" color="info" size="small" variant="outlined" />
                  )}
                  {isUnplannedLeave(leave) && (
                    <Chip label="Unplanned" color="warning" size="small" variant="outlined" />
                  )}
                </Stack>
              </Box>

              <Divider />

              {/* Leave Details Grid */}
              <Grid container spacing={2} gap={1}>
                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Employee Name
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {leave.employee?.userName || leave.employee?.email || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Leave Type
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {LEAVE_TYPE_LABELS[leave.leaveType] || leave.leaveType}
                    {leave.isHalfDay &&
                      ` (Half Day - ${leave.halfDayType === 'first_half' ? 'First Half' : 'Second Half'})`}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    From Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {formatDate(leave.startDate)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    To Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {formatDate(leave.endDate)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Total Days
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {leave.totalDays} {leave.totalDays === 1 ? 'day' : 'days'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Requested Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {formatDate(leave.createdAt)}
                  </Typography>
                </Grid>

                {isSystemApprovedLeave(leave) && (
                  <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                    <Typography variant="body2" color="text.secondary">
                      Approval Source
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                      System (Auto Approved)
                    </Typography>
                  </Grid>
                )}

                {leave.reason && (
                  <Grid item xs={12} className={classes.detailIndCard}>
                    <Typography variant="body2" color="text.secondary">
                      Reason
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {isSystemApprovedLeave(leave) ? 'System generated leave' : leave.reason}
                    </Typography>
                  </Grid>
                )}

                {leave.reviewers && Array.isArray(leave.reviewers) && leave.reviewers.length > 0 && (
                  <Grid item xs={12} className={classes.detailIndCard}>
                    <Typography variant="body2" color="text.secondary">
                      Selected Reviewers
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {leave.reviewerDetails && leave.reviewerDetails.length > 0
                        ? leave.reviewerDetails.map((reviewer) => {
                            const reviewerName = reviewer.userName || reviewer.email || `User ${reviewer.id}`;
                            return (
                              <Chip
                                key={reviewer.id}
                                label={reviewerName}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            );
                          })
                        : leave.reviewers.map((reviewerId) => (
                              <Chip
                                key={reviewerId}
                                label={`User ${reviewerId}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      These reviewers have been notified via email
                    </Typography>
                  </Grid>
                )}

                {leave.reviewedBy && (
                  <>
                    <Grid item xs={12} md={6} className={classes.detailIndCard}>
                      <Typography variant="body2" color="text.secondary">
                        Reviewed By
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {leave.approver && leave.approver.id === userId
                          ? 'Me'
                          : leave.approver?.userName || leave.approver?.email || '-'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6} className={classes.detailIndCard}>
                      <Typography variant="body2" color="text.secondary">
                        Reviewed Date
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {formatDate(leave.reviewedAt)}
                      </Typography>
                    </Grid>
                  </>
                )}

                {leave.rejectionReason && (
                  <Grid item xs={12} className={classes.detailIndCard}>
                    <Typography variant="body2" color="text.secondary">
                      Rejection Reason
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, color: 'error.main', whiteSpace: 'pre-wrap' }}>
                      {leave.rejectionReason}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* Action Buttons */}
              {(canApproveReject || canCancel || canCancelApproved || canAdminEdit) && (
                <>
                  <Divider />
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    {canAdminEdit && (
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<Iconify icon="eva:edit-fill" />}
                        onClick={() => leave?.status === 'approved' ? setEditConfirmDialogOpen(true) : setEditFormOpen(true)}
                      >
                        Edit Leave
                      </Button>
                    )}
                    {canCancel && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Iconify icon="material-symbols:delete" />}
                        onClick={() => setCancelDialogOpen(true)}
                      >
                        Delete Leave
                      </Button>
                    )}
                    {canCancelApproved && (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<Iconify icon="eva:close-circle-fill" />}
                        onClick={() => setCancelApprovedDialogOpen(true)}
                      >
                        Cancel Leave
                      </Button>
                    )}
                    {canApproveReject && (
                      <>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Iconify icon="eva:close-circle-fill" />}
                          onClick={() => setRejectDialogOpen(true)}
                        >
                          Reject
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                          onClick={() => setApproveDialogOpen(true)}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                  </Stack>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <EmployeePlanDialog
        open={planDialogOpen}
        onClose={() => setPlanDialogOpen(false)}
        request={leave}
        kind="leave"
        showWfh={isWfhCompany() && checkComponentPermission('READ_WFH')}
      />

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Approve Leave Request</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to approve this leave request?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <LoadingButton variant="contained" color="success" onClick={handleApprove} loading={isSubmitting}>
            Approve
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog with Reason */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Leave Request</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Please provide a reason for rejecting this leave request:</Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter the reason for rejection..."
            required
            error={!rejectionReason.trim() && rejectionReason.length > 0}
            helperText={!rejectionReason.trim() && rejectionReason.length > 0 ? 'Rejection reason is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectionReason('');
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={handleReject}
            loading={isSubmitting}
            disabled={!rejectionReason.trim()}
          >
            Reject
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Delete Leave Request</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this leave request? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
          <LoadingButton variant="contained" color="error" onClick={handleCancel} loading={isSubmitting}>
            Yes, Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Edit Approved Leave Confirmation Dialog */}
      <Dialog open={editConfirmDialogOpen} onClose={() => setEditConfirmDialogOpen(false)}>
        <DialogTitle>Edit Approved Leave</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to update this approved leave request?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditConfirmDialogOpen(false)}>No</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setEditConfirmDialogOpen(false);
              setEditFormOpen(true);
            }}
          >
            Yes, Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Edit Leave Form */}
      {editFormOpen && (
        <LeaveRequestForm
          open
          onClose={() => setEditFormOpen(false)}
          onSubmit={handleEditSubmit}
          leaveData={leave}
          isEdit
        />
      )}

      {/* Cancel Approved Leave Dialog */}
      <Dialog
        open={cancelApprovedDialogOpen}
        onClose={() => {
          setCancelApprovedDialogOpen(false);
          setCancellationReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Approved Leave</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to cancel this approved leave? The employee will be notified.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            label="Cancellation Reason (optional)"
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder="Enter the reason for cancellation..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelApprovedDialogOpen(false);
              setCancellationReason('');
            }}
          >
            No
          </Button>
          <LoadingButton variant="contained" color="warning" onClick={handleCancelApproved} loading={isSubmitting}>
            Yes, Cancel Leave
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const LeaveDetails = () => (
  <QueryClientProvider client={queryClient}>
    <LeaveDetailsContent />
  </QueryClientProvider>
);

export default LeaveDetails;
